// consent.service.ts
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';


export interface ConsentClient {
  FirstName: string;
  LastName: string;
  Phone: string;
  IDNo: string;
}
export interface ConsentItem {
  id: string;
  name?: string;
  mandatory?: boolean;
  selected?: boolean;
}
export interface ConsentConfig {
  channelId: number;
  client: ConsentClient;
  appId: string;
  appPass: string;
  countryID?: string;
  persist?: boolean;
  onSubmit: (selectedConsents: ConsentItem[], allConsents: ConsentItem[]) => void;
  onClose: () => void;
}


@Injectable({ providedIn: 'root' })

export class ConsentService {
  private consentScriptLoaded = false;
  private consentInstance: any;
  private scriptLoadCallbacks: (() => void)[] = [];

  constructor() {}

  /**
   * Opens the consent form with the provided options
   * @param options ConsentConfig configuration
   */
  loadConsentForm(options: ConsentConfig): void {
    if (!this.consentScriptLoaded) {
      this.loadConsentScript(() => this.initializeConsentForm(options));
      return;
    }
    
    this.initializeConsentForm(options);
  }

  /**
   * Loads the consent script if not already loaded
   * @param callback Function to call after script is loaded
   */
  private loadConsentScript(callback: () => void): void {
    // If script is already loading, just add the callback to the queue
    if (this.scriptLoadCallbacks.length > 0) {
      this.scriptLoadCallbacks.push(callback);
      return;
    }

    // First time loading
    this.scriptLoadCallbacks.push(callback);

    const timestamp = new Date().getTime();
    const cacheBuster = `?v=${timestamp}`;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = environment.consentUrl + cacheBuster; // define consentUrl in environments
    script.async = true;
    
    script.onload = () => {
      this.consentScriptLoaded = true;
      // Execute all queued callbacks
      this.scriptLoadCallbacks.forEach(cb => cb());
      this.scriptLoadCallbacks = [];
    };
    
    script.onerror = () => {
      console.error('Failed to load consent script');
      this.scriptLoadCallbacks = [];
    };

    document.body.appendChild(script);
  }

  /**
   * Initializes the consent form with the provided options
   * @param options ConsentConfig configuration
   */
  private initializeConsentForm(config: ConsentConfig): void {
    if (!window.Consent || !window.Consent.init) {
      console.error('Consent script not properly loaded');
      return;
    }

    this.consentInstance = window.Consent.init(config);
  }

  /**
   * Closes the consent form if open
   */
  closeConsentForm(): void {
    if (this.consentInstance && typeof this.consentInstance.close === 'function') {
      this.consentInstance.close();
    }
  }
}
